import { useNuxtApp } from '#imports'
import type { RouteLocationNormalized } from 'vue-router'

import { useAddGradeInUrl } from '~/scopes/product-core/components/ProductDetailPageBase/composables/useRedirections/composables/useAddGradeInUrl/useAddGradeInUrl'
import { useGradeFallback } from '~/scopes/product-core/components/ProductDetailPageBase/composables/useRedirections/composables/useGradeFallback/useGradeFallback'
import { useInvalidGradeFallback } from '~/scopes/product-core/components/ProductDetailPageBase/composables/useRedirections/composables/useInvalidGradeFallback/useInvalidGradeFallback'
import { useProductRematch } from '~/scopes/product-core/components/ProductDetailPageBase/composables/useRedirections/composables/useProductRematch/useProductRematch'
import type { useFetchPickers } from '~/scopes/product-core/composables/useFetch/useFetchPickers/useFetchPickers'
import type { useFetchProduct } from '~/scopes/product-core/composables/useFetch/useFetchProduct/useFetchProduct'

/**
 * Orchestrates every URL-correcting redirect on the Product Detail Page.
 *
 * Called by the `fetchDataAndRunRedirectionsIfNeeded` route middleware after awaiting both
 * product and pickers fetches. By the time this runs, all refs are populated —
 * no need to fetch or await here.
 *
 * ## Nuxt context and `runWithContext`
 *
 * Nuxt's synchronous context is lost after every `await` boundary (unless
 * `experimental.asyncContext` is enabled). Each sub-composable is wrapped in
 * `nuxtApp.runWithContext()` so it receives a fresh context reference before
 * calling `useNuxtApp()`-dependent APIs (`useLogger`, `useNuxtData`, `navigateTo`).
 *
 * `nuxtApp` is captured synchronously at function start while the outer
 * `runWithContext` call in the middleware still has context active.
 *
 * ## Ordering — sequential, not parallel
 *
 * The redirect checks must run in this exact order:
 *   1. `useInvalidGradeFallback` — remove a structurally invalid grade (e.g. `l=999`)
 *      before step 2 attempts to add a default one.
 *   2. `useAddGradeInUrl` — add the default grade when none is present.
 *   3. `useProductRematch` — correct a stale product slug (301).
 *   4. `useGradeFallback` — replace an unavailable grade with the first available one.
 *
 */
export async function useRedirections(
  productFetch: ReturnType<typeof useFetchProduct>,
  pickersFetch: ReturnType<typeof useFetchPickers>,
  route: RouteLocationNormalized,
) {
  const nuxtApp = useNuxtApp()

  // 302: Remove structurally invalid grades before the missing-grade check below
  const invalidGrade = await nuxtApp.runWithContext(() =>
    useInvalidGradeFallback(route),
  )
  if (invalidGrade) return invalidGrade

  // 302: Add the default grade when the URL has none (needs resolved selectedOffer)
  const missingGrade = await nuxtApp.runWithContext(() =>
    useAddGradeInUrl(pickersFetch.selectedOffer, route),
  )
  if (missingGrade) return missingGrade

  // 301: Redirect to the canonical slug/uuid if the product was rematched
  const rematched = await nuxtApp.runWithContext(() =>
    useProductRematch(productFetch.data, route),
  )
  if (rematched) return rematched

  // 302: Replace an unavailable grade with the default-selected one (or first available)
  const fallbackGrade = await nuxtApp.runWithContext(() =>
    useGradeFallback(pickersFetch.data, route),
  )
  if (fallbackGrade) return fallbackGrade

  return undefined
}
